/**
 * Zugriff auf die eigene Schnittstelle. Ein Weg fuer alle Aufrufe, damit
 * Fehlerbehandlung und Abmeldung bei abgelaufener Sitzung an einer Stelle
 * stehen und nicht in jeder Ansicht neu geschrieben werden.
 */

import type { Art } from './art'

export class ApiFehler extends Error {
  constructor(
    readonly status: number,
    override readonly message: string,
    readonly feld?: string,
  ) {
    super(message)
    this.name = 'ApiFehler'
  }
}

type Verfahren = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

async function anfragen<T>(verfahren: Verfahren, pfad: string, koerper?: unknown): Promise<T> {
  const optionen: RequestInit = { method: verfahren, credentials: 'same-origin' }
  if (koerper instanceof FormData) {
    optionen.body = koerper
  } else if (koerper !== undefined) {
    optionen.headers = { 'content-type': 'application/json' }
    optionen.body = JSON.stringify(koerper)
  }

  let antwort: Response
  try {
    antwort = await fetch(pfad, optionen)
  } catch {
    throw new ApiFehler(0, 'Der Server ist nicht erreichbar.')
  }

  if (antwort.status === 204) return undefined as T

  const inhalt = antwort.headers.get('content-type') ?? ''
  const daten: unknown = inhalt.includes('application/json') ? await antwort.json() : await antwort.text()

  if (!antwort.ok) {
    const nachricht =
      typeof daten === 'object' && daten !== null && 'fehler' in daten && typeof daten.fehler === 'string'
        ? daten.fehler
        : 'Es ist ein Fehler aufgetreten.'
    const feld =
      typeof daten === 'object' && daten !== null && 'feld' in daten && typeof daten.feld === 'string'
        ? daten.feld
        : undefined
    // Abgelaufene Sitzung: die Oberflaeche soll nicht bei jedem Klick
    // "Nicht angemeldet" sagen, sondern zur Anmeldung zurueck. Die Anmeldung
    // selbst ist ausgenommen, dort heisst 401 "falsches Passwort".
    if (antwort.status === 401 && !pfad.startsWith('/api/anmeldung')) {
      window.dispatchEvent(new Event('sitzung-abgelaufen'))
    }
    throw new ApiFehler(antwort.status, nachricht, feld)
  }

  return daten as T
}

export const api = {
  holen: <T>(pfad: string) => anfragen<T>('GET', pfad),
  senden: <T>(pfad: string, koerper?: unknown) => anfragen<T>('POST', pfad, koerper),
  aendern: <T>(pfad: string, koerper: unknown) => anfragen<T>('PATCH', pfad, koerper),
  /** Ganz ersetzen. Bisher nur fuer die DATEV-Stammdaten der Karten. */
  ersetzen: <T>(pfad: string, koerper: unknown) => anfragen<T>('PUT', pfad, koerper),
  loeschen: <T>(pfad: string) => anfragen<T>('DELETE', pfad),
}

/* ─────────────────────────────────────────── Gestalten der Schnittstelle */

export type Rolle = 'verwaltung' | 'kanzlei'
export type AuszugStand = 'pruefen' | 'bereit' | 'freigegeben' | 'doppelt' | 'exportiert'
export type DokumentStand = 'wartet' | 'wird_gelesen' | 'gelesen' | 'fehler'

export interface Benutzer {
  id: string
  email: string
  name: string
  rolle: Rolle
}

export interface Sitzungsauskunft {
  benutzer: Benutzer
  kanzlei: string | null
  loeschfristTage: number | null
  leserEingerichtet: boolean
}

export interface AuszugKurz {
  id: string
  dokument_id: string
  reihenfolge: number
  iban: string | null
  bank: string | null
  inhaber: string | null
  auszug_nummer: string | null
  zeitraum_von: string | null
  zeitraum_bis: string | null
  stand: AuszugStand
  mandant_id: string | null
  mandant_name: string | null
  nachgelesen: boolean
  buchungen: number
  art: Art
  /** Woran der Trennlauf die Art erkannt hat, bei Karten die maskierte Kartennummer. */
  art_beleg: string | null
  /** Die Art ist nicht bestaetigt. Steht dann auf beiden Seiten und blockt. */
  art_unsicher: boolean
}

export interface Eingangsdokument {
  id: string
  dateiname: string
  seiten: number | null
  hat_textebene: boolean | null
  stand: DokumentStand
  fehler: string | null
  eingegangen_am: string
  gelesen_am: string | null
  auszuege: AuszugKurz[]
}

export interface Befund {
  art: string
  blockt: boolean
  freigebbar: boolean
  titel: string
  text: string
  positionen?: number[]
  bezug?: string
}

export interface Buchung {
  id: string
  position: number
  buchungsdatum: string | null
  valuta: string | null
  betrag_cent: string
  richtung: 'CRDT' | 'DBIT'
  betrag_gedruckt: string | null
  buchungstext: string | null
  gegenpartei: string | null
  gegen_iban: string | null
  gegen_bic: string | null
  verwendungszweck: string | null
  end_to_end_id: string | null
  mandatsreferenz: string | null
  glaeubiger_id: string | null
  seite: number | null
  von_hand: boolean
  nachgelesen: boolean
}

export interface AuszugVoll {
  id: string
  dokument_id: string
  mandant_id: string | null
  zuordnung: 'iban' | 'hand' | 'karte' | null
  seiten: number[]
  iban: string | null
  bic: string | null
  bank: string | null
  inhaber: string | null
  auszug_nummer: string | null
  waehrung: string
  zeitraum_von: string | null
  zeitraum_bis: string | null
  anfangssaldo_cent: string | null
  schlusssaldo_cent: string | null
  summe_soll_gedruckt_cent: string | null
  summe_haben_gedruckt_cent: string | null
  stand: AuszugStand
  freigabe_grund: string | null
  freigegeben_am: string | null
  nachgelesen: boolean
  bezeichnung: string
  art: Art
  art_beleg: string | null
  art_unsicher: boolean
}

export interface Saldo {
  summeHaben: string
  summeSoll: string
  bewegung: string
  errechnet: string | null
  gehtAuf: boolean | null
  differenz: string | null
}

export interface Auszugansicht {
  auszug: AuszugVoll
  buchungen: Buchung[]
  dokument: { id: string; dateiname: string; seiten: number | null; hat_textebene: boolean | null; eingegangen_am: string }
  mandant: { id: string; name: string; mandantennummer: number | null } | null
  kontoGehoertZu: { id: string; name: string } | null
  geschwister: { id: string; reihenfolge: number }[]
  befunde: Befund[]
  saldo: Saldo | null
}

export interface MandantZeile {
  id: string
  name: string
  mandantennummer: number | null
  konten: number
  zu_pruefen: number
  bereit: number
  letzter_export: string | null
}

export interface Bankkonto {
  id: string
  iban: string
  bic: string | null
  bank: string | null
  inhaber: string | null
  rz_umsaetze_ab: string | null
}

export interface ExportZeile {
  id: string
  format: 'camt053' | 'mt940' | 'datev_extf'
  version: string | null
  dateiname: string
  anzahl_auszuege: number
  anzahl_buchungen: number
  zeitraum_von: string | null
  zeitraum_bis: string | null
  erzeugt_am: string
  ibans: string[]
  mandant_id?: string
  mandant_name?: string
  mandantennummer?: number | null
  erzeugt_von?: string | null
  art?: Art
}

export interface Mandantenakte {
  mandant: { id: string; name: string; mandantennummer: number | null; notiz: string | null }
  konten: Bankkonto[]
  auszuege: (Omit<AuszugKurz, 'dokument_id' | 'reihenfolge' | 'inhaber' | 'mandant_id' | 'mandant_name'> & {
    dokument_id: string
    dateiname: string
    anfangssaldo_cent: string | null
    schlusssaldo_cent: string | null
    export_id: string | null
    bezeichnung: string
  })[]
  exporte: ExportZeile[]
}

/* ─────────────────────────────────────────── Kreditkarten */

export type SaldoArt = 'schuld' | 'guthaben' | 'unklar'
export type Kartenklasse = 'kauf' | 'bargeld' | 'entgelt' | 'zins' | 'zahlung' | 'gutschrift' | 'ruecklastschrift' | 'sonstig'

/** Der gelesene Kopf einer Kreditkartenabrechnung. Cent als Zeichenkette, Schuld ohne Vorzeichen. */
export interface Kartenabrechnung {
  auszug_id: string
  kreditkarte_id: string | null
  kennung: string | null
  anbieter_gedruckt: string | null
  endziffern: string | null
  kartenart: 'charge' | 'revolving' | 'unklar' | null
  anfangssaldo_gedruckt: string | null
  schlusssaldo_gedruckt: string | null
  anfangssaldo_art: SaldoArt | null
  schlusssaldo_art: SaldoArt | null
  saldo_beleg: string | null
  legende: string | null
  abrechnungsdatum: string | null
  faelliger_betrag_cent: string | null
  mindestbetrag_cent: string | null
  ausgleich_konto: string | null
  ausgleich_datum: string | null
  ausgleich_betrag_cent: string | null
  hinweis: string | null
  gelesen_am: string
}

/** Was nur eine Kartenbuchung hat, je buchung_id. */
export interface KartenZusatz {
  buchung_id: string
  belegdatum: string | null
  klasse: Kartenklasse | null
  originalbetrag_cent: string | null
  originalwaehrung: string | null
  kurs: string | null
  endziffern: string | null
}

export interface KartenVorschlag {
  kreditkarteId: string
  mandantId: string
  mandantName: string
  bezeichnung: string
  text: string
}

/** Antwort von GET /api/karten/abrechnung/:id. abrechnung null heisst: noch nicht gelesen. */
export interface Kartenansicht {
  abrechnung: Kartenabrechnung | null
  kennungLesbar: string
  zusatz: Record<string, KartenZusatz>
  vorschlag: KartenVorschlag | null
}

export interface Kreditkarte {
  id: string
  kennung: string
  anbieter: string
  endziffern: string
  bezeichnung: string | null
  inhaber: string | null
  datev_konto: number | null
  datev_gegenkonto: number | null
  /** Feste Ersatz-IBAN der Karte fuer den camt-Weg (Bankleitzahl 99999999). */
  ersatz_iban?: string
  erstellt_am: string
}

export interface DatevStamm {
  beraternummer: number
  wj_beginn_monat: number
  sachkontenlaenge: number
  geaendert_am?: string
}

/** DATEV-Vorgabe der Kanzlei: jeder Mandant und jede Karte erbt sie, Abweichungen gehen vor. */
export interface DatevVorgabe extends DatevStamm {
  kartenkonto: number | null
  gegenkonto: number | null
}

export interface MandantKarten {
  karten: Kreditkarte[]
  stamm: DatevStamm | null
  vorgabe: DatevVorgabe | null
  vorschlagBeraternummer: number | null
}

export interface KartenvorschauAbrechnung {
  id: string
  bezeichnung: string
  stand: AuszugStand
  zeitraum_von: string | null
  zeitraum_bis: string | null
  anzahlBuchungen: number
  anfangssaldo_cent: string | null
  schlusssaldo_cent: string | null
}

export interface KartenvorschauKarte {
  karte: Pick<Kreditkarte, 'id' | 'kennung' | 'anbieter' | 'endziffern' | 'bezeichnung' | 'datev_konto' | 'datev_gegenkonto'> | null
  kennung: string | null
  kennungLesbar: string
  bereit: KartenvorschauAbrechnung[]
  nichtBereit: KartenvorschauAbrechnung[]
  anzahlBuchungen: number
  nullbuchungen: number
  hindernisse: string[]
  /** camt: feste Ersatz-IBAN der Karte (Bankleitzahl 99999999), Version und was dem entgegensteht. */
  ersatzIban: string | null
  camtVersion: '02' | '08'
  camtVersionFestgelegt: boolean
  camtHindernisse: string[]
}

/** Antwort von GET /api/mandanten/:id/kartenexport. */
export interface Kartenvorschau {
  karten: KartenvorschauKarte[]
  stamm: DatevStamm | null
  mandantennummer: number | null
}
